import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2, X, Pencil } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { title: "", slug: "", excerpt: "", cover_image: "", video_url: "", body: "", author: "HK Forexedge Desk", status: "published" };

export default function AdminInsights() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/insights").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/admin/insights/${editing.id}`, payload);
      else await api.post("/admin/insights", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/insights/${id}`);
    toast.success("Deleted");
    load();
  };

  const F = ({ label, k, textarea }) => (
    <div>
      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">{label}</label>
      {textarea ? (
        <textarea rows={6} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
          data-testid={`insight-${k}`} className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
      ) : (
        <input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
          data-testid={`insight-${k}`} className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
      )}
    </div>
  );

  return (
    <div data-testid="admin-insights">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Content</p>
          <h1 className="font-editorial text-3xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-4xl">Insights</h1>
          <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Write and publish articles. Supports an optional cover image and video.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="insight-new" className="btn-edge text-sm"><Plus size={14} /> New article</button>
      </div>

      <div className="overflow-hidden rounded-md border border-[color:var(--ink-hairline)] bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-[color:var(--ink-hairline)] text-left text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">
            <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[color:var(--ink-hairline)] last:border-0">
                <td className="px-5 py-3"><p className="font-medium text-[color:var(--ink)]">{r.title}</p><p className="text-[11px] text-[color:var(--ink-muted)]">{r.slug}</p></td>
                <td className="px-5 py-3"><span className={`chip ${r.status === "published" ? "chip-emerald" : "chip-neutral"}`}>{r.status}</span></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(r)} data-testid={`insight-edit-${r.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--ink-body)] hover:border-[color:var(--ink)]"><Pencil size={14} /></button>
                    <button onClick={() => remove(r.id)} data-testid={`insight-delete-${r.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--alert)] hover:border-[color:var(--alert)]"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-[color:var(--ink-muted)]">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-[color:var(--ink-hairline)] bg-[color:var(--paper)] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-editorial text-xl font-semibold text-[color:var(--ink)]">{editing.id ? "Edit article" : "New article"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid gap-4">
              <F label="Title" k="title" />
              <div className="grid gap-4 md:grid-cols-2">
                <F label="Slug (optional)" k="slug" />
                <F label="Author" k="author" />
              </div>
              <F label="Excerpt" k="excerpt" />
              <div className="grid gap-4 md:grid-cols-2">
                <F label="Cover image URL" k="cover_image" />
                <F label="Video URL (optional)" k="video_url" />
              </div>
              <F label="Body" k="body" textarea />
              <div>
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="insight-status"
                  className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)]">
                  <option value="published">published</option>
                  <option value="draft">draft</option>
                </select>
              </div>
            </div>
            <button disabled={saving} data-testid="insight-save" className="btn-edge mt-6 w-full justify-center">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save article
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
